
import BaseButton from "@/components/base/BaseButton.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import BaseDropdown from "@/components/base/BaseDropdown.vue";
import BaseSection from "@/components/base/BaseSection.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import { DropdownOption } from "@/models/DropdownOption";
import { Charity } from "@/models/www/Charity";
import { useCharityStore } from "@/store/charity";
import {
	computed,
	defineComponent,
	PropType,
	reactive,
	ref,
	watch,
} from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers, email, required, requiredIf } from "@vuelidate/validators";

export default defineComponent({
	components: {
		BaseSection,
		BaseDropdown,
		BaseButton,
		BaseCheckbox,
		BaseInput,
	},
	props: {
		charity: Object as PropType<Charity>,
	},
	setup(props) {
		const store = useCharityStore();
		const error = ref("");
		const donationOptions = props.charity?.resources.map((r) => ({
			value: r.resourceCode,
			label: `£${r.amount}`,
		})) as DropdownOption[];

		const getIdentifier = () => {
			if(props.charity) {
				return props.charity.resources.find(r => r.resourceCode === state.resourceCode)?.identifier ?? ""; 
			}

			return undefined;
		};

		const getSelectedResource = () => {
			return store.getResources.find((r) =>
				props.charity?.resources
					.map((resource) => resource.resourceCode)
					.includes(r.resourceCode)
			);
		};
		
		const noDonation = computed(() => {
			return (
				store.getNoDonation.find(
					(d) => d === props.charity?.content?.resource_prefix
				) ?? false
			);
		});

		const charityAdded = computed(() => {
			return getSelectedResource()?.resourceCode;
		});

		const state = reactive({
			resourceCode: getSelectedResource()?.resourceCode,
			giftAid: getSelectedResource()?.giftAid ?? false,
			noDonation: noDonation.value,
			email: getSelectedResource()?.email ?? '',
			newsletter: (getSelectedResource()?.email ?? '').length > 0,
		});

		const rules = {
			resourceCode: {
				v: helpers.withMessage(
					() => "Please make a choice",
					() => (charityAdded.value || noDonation.value) as any
				),
			},
			giftAid: {},
			noDonation: {},
			newsletter: {},
			email : {
				v: helpers.withMessage(
					() => "You must enter a valid e-mail address for the newsletter",
					email
				),
				v2: helpers.withMessage(
					() => "You must enter a valid e-mail address for the newsletter",
					requiredIf(function() {
						return state.newsletter === true
					})
				)
				
			}
		};

		const v$ = useVuelidate(rules, state);

		const handleAddDonation = () => {
			if (state.resourceCode) {
				addDonation();
			} else {
				error.value = "Please select an amount";
			}
		};

		const addDonation = () => {
			error.value = "";

			if (props.charity?.content?.resource_prefix) {
				store.removeNoDonationResource(props.charity.content.resource_prefix);
				store.addResource({
					identifier: getIdentifier() as string,
					prefix: props.charity?.content?.resource_prefix,
					giftAid: state.giftAid,
					resourceCode: state.resourceCode as string,
					email: state.email as string,
				});
			}
		};

		const removeDonation = () => {
			store.removeResource(props.charity?.content?.resource_prefix ?? "");
			store.addNoDonationResource(
				props.charity?.content?.resource_prefix ?? ""
			);
			state.giftAid = false;
			state.resourceCode = "";
			state.email = '';
			error.value = "";
		};

		watch(state, (current, previous) => {
			if (current.resourceCode) {
				addDonation();
			}
		});
		
		const noNewsletter = props.charity?.content?.newsletter !== true;
		
		return {
			addDonation,
			removeDonation,
			handleAddDonation,
			donationOptions,
			v$,
			charityAdded,
			error,
			noDonation,
			noNewsletter,
		};
	},
});
